<script setup lang="ts">
/**
 * The wall for `NxControlTile`s: an auto-filling grid that fits as many
 * tiles per row as the width allows, down to one column on a phone. Tiles of
 * one row share its height, so their controls line up at the bottom.
 *
 * `size` is the default for every tile inside (a tile's own `size` wins) and
 * tightens the gap; `minTileWidth` overrides the minimum tile width. With a
 * `label` (a room, a floor, a machine group) the grid is a named `group` for
 * screen readers.
 *
 * With a `title` the grid shows that name as its heading instead, and the
 * heading names the group: `headingLevel` fits it into the page outline,
 * `icon` sits before it, `meta` is a quiet note after it („3 Elemente“) and
 * the `actions` slot holds small buttons at the end of the heading row. When
 * there is nothing to show, `empty` (or the `empty` slot, e.g. with an add
 * button) replaces the tiles with a hint across the whole row.
 */
import { computed, provide, useId, useSlots, type Component } from 'vue';
import { controlTileGridKey, type NxControlTileSize } from './context';

const props = withDefaults(
  defineProps<{
    size?: NxControlTileSize;
    /** Minimum width of a tile, as a CSS length. Defaults to `--nxt-panel-width-xs`. */
    minTileWidth?: string;
    /** Accessible name of the group, e.g. the room (no visible heading). */
    label?: string;
    /** Visible heading of the group; it names the group, `label` is then not needed. */
    title?: string;
    /** Level of the `title` heading in the page outline. */
    headingLevel?: 2 | 3 | 4 | 5 | 6;
    /** Icon component before the title. */
    icon?: Component;
    /** Quiet note after the title, e.g. a count. */
    meta?: string;
    /** Hint shown instead of the tiles (there are none); the `empty` slot replaces it. */
    empty?: string;
  }>(),
  {
    size: 'regular',
    minTileWidth: undefined,
    label: undefined,
    title: undefined,
    headingLevel: 3,
    icon: undefined,
    meta: undefined,
    empty: undefined,
  }
);

defineSlots<{
  default?(): unknown;
  /** Small buttons at the end of the heading row (needs `title`). */
  actions?(): unknown;
  /** Replaces the tiles with a hint, e.g. with an add button. */
  empty?(): unknown;
}>();

provide(controlTileGridKey, { size: computed(() => props.size) });

const slots = useSlots();
const headingId = `nx-control-tile-grid-${useId()}`;
const showEmpty = computed(() => props.empty !== undefined || !!slots.empty);

const style = computed(() =>
  props.minTileWidth ? { '--control-tile-min': props.minTileWidth } : undefined
);
const role = computed(() => (props.title || props.label ? 'group' : undefined));
</script>

<template>
  <div v-if="props.title" class="nx-control-tile-wall" data-testid="nx-control-tile-wall">
    <div class="nx-control-tile-wall__head">
      <component :is="`h${props.headingLevel}`" :id="headingId" class="nx-control-tile-wall__title">
        <component
          :is="props.icon"
          v-if="props.icon"
          class="nx-control-tile-wall__icon"
          aria-hidden="true"
        />
        <span class="nx-control-tile-wall__title-text">{{ props.title }}</span>
      </component>
      <span v-if="props.meta" class="nx-control-tile-wall__meta">{{ props.meta }}</span>
      <div v-if="$slots.actions" class="nx-control-tile-wall__actions">
        <slot name="actions" />
      </div>
    </div>
    <div
      class="nx-control-tile-grid"
      :class="`size-${props.size}`"
      :style="style"
      :role="role"
      :aria-labelledby="headingId"
      data-testid="nx-control-tile-grid"
    >
      <div
        v-if="showEmpty"
        class="nx-control-tile-grid__empty"
        data-testid="nx-control-tile-grid-empty"
      >
        <slot name="empty">{{ props.empty }}</slot>
      </div>
      <slot v-else />
    </div>
  </div>
  <div
    v-else
    class="nx-control-tile-grid"
    :class="`size-${props.size}`"
    :style="style"
    :role="role"
    :aria-label="props.label"
    data-testid="nx-control-tile-grid"
  >
    <div
      v-if="showEmpty"
      class="nx-control-tile-grid__empty"
      data-testid="nx-control-tile-grid-empty"
    >
      <slot name="empty">{{ props.empty }}</slot>
    </div>
    <slot v-else />
  </div>
</template>

<style scoped>
.nx-control-tile-grid {
  /* Two regular tiles side by side on a phone. */
  --control-tile-min: var(--nxt-panel-width-xs);

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, var(--control-tile-min)), 1fr));
  gap: var(--nxt-space-sm);
  align-items: stretch;
}
.nx-control-tile-grid.size-compact {
  gap: var(--nxt-space-xs);
}

/* ── Heading (with `title`) ──────────────────────────────────────────── */
.nx-control-tile-wall {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  min-width: 0;
}
.nx-control-tile-wall__head {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
  min-height: var(--nxt-control-size-sm);
}
.nx-control-tile-wall__title {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
  margin: 0;
  color: var(--nxt-heading-fg);
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
}
.nx-control-tile-wall__title-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-control-tile-wall__icon {
  flex: none;
  width: var(--nxt-icon-size-md);
  height: var(--nxt-icon-size-md);
  color: var(--nxt-fg-muted);
}
.nx-control-tile-wall__meta {
  flex: none;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.nx-control-tile-wall__actions {
  display: flex;
  flex: none;
  align-items: center;
  gap: var(--nxt-space-2xs);
  margin-inline-start: auto;
}

/* ── Empty hint ──────────────────────────────────────────────────────── */
/* Across the whole row, in the tiles' shape but without their surface: an
   invitation, not a tile. */
.nx-control-tile-grid__empty {
  display: flex;
  flex-wrap: wrap;
  grid-column: 1 / -1;
  align-items: center;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border: var(--nxt-hairline) dashed var(--nxt-border-strong);
  border-radius: var(--nxt-radius-xl);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}
</style>
